<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          <span>Edit Chat</span>
        </v-card-title>
        <v-card-text>
          <div v-if="isLoading" class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
            <p class="mt-2">Loading chat...</p>
          </div>
          <div v-else-if="!chat && !isLoading" class="text-center">
            <v-icon large color="error">mdi-alert-circle</v-icon>
            <p class="mt-2">Chat not found</p>
            <v-btn color="primary" @click="$router.back()">Go Back</v-btn>
          </div>
          <div v-else>
            <v-row>
              <v-col sm="6">
                <v-switch
                  class="mt-0 ml-5"
                  v-model="form.is_group"
                  inset
                  :label="`Chat Type: ${form.is_group ? 'Group Chat' : 'Direct Chat'}`"
                ></v-switch>
              </v-col>
              <v-col sm="6">
                <v-switch
                  class="mt-0 ml-5"
                  v-model="form.is_active"
                  inset
                  :label="`Status: ${form.is_active ? 'Active' : 'Inactive'}`"
                ></v-switch>
              </v-col>
              <v-col sm="12" v-if="form.is_group">
                <v-text-field
                  v-model="form.name"
                  solo
                  rounded
                  label="Chat Name *"
                  prepend-inner-icon="mdi-message-text"
                  :error-messages="errors.name"
                  placeholder="Enter group chat name..."
                ></v-text-field>
              </v-col>
              <v-col sm="12" v-if="form.is_group">
                <v-text-field
                  v-model="form.image_path"
                  solo
                  rounded
                  label="Image Path"
                  prepend-inner-icon="mdi-image"
                  :error-messages="errors.image_path"
                  placeholder="Enter image URL or path..."
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Chat Preview Card -->
            <v-card outlined class="mt-4">
              <v-card-title>
                <v-icon left>mdi-eye</v-icon>
                Chat Preview
              </v-card-title>
              <v-card-text>
                <v-row align="center">
                  <v-col cols="auto">
                    <v-avatar size="60" :color="form.image_path ? 'transparent' : 'grey lighten-2'">
                      <v-img v-if="form.image_path" :src="form.image_path" alt="Chat Image"></v-img>
                      <v-icon v-else size="30" color="grey">
                        {{ form.is_group ? 'mdi-account-group' : 'mdi-account' }}
                      </v-icon>
                    </v-avatar>
                  </v-col>
                  <v-col>
                    <div class="subtitle-1 font-weight-medium">
                      {{ form.name || (form.is_group ? 'Group Chat' : 'Direct Chat') }}
                    </div>
                    <div class="caption grey--text">
                      {{ form.is_group ? 'Group Chat' : 'Direct Chat' }} • 
                      {{ form.is_active ? 'Active' : 'Inactive' }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Chat Information Card -->
            <v-card class="mt-4" outlined>
              <v-card-title>
                <v-icon left>mdi-information</v-icon>
                Chat Information
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col sm="3">
                    <strong>Created:</strong><br />
                    {{ formatDate(chat.created_at) }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Last Updated:</strong><br />
                    {{ formatDate(chat.updated_at) }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Created By:</strong><br />
                    {{
                      chat.created_by
                        ? chat.created_by.user_name
                        : "N/A"
                    }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Status:</strong><br />
                    <v-chip
                      small
                      :color="form.is_active ? 'success' : 'error'"
                      text-color="white"
                    >
                      {{ form.is_active ? "Active" : "Inactive" }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-divider class="mt-5 mb-5"></v-divider>
            <v-row>
              <v-col sm="12">
                <v-btn
                  :loading="isSaving"
                  :disabled="isSaving"
                  color="primary"
                  @click="updateData"
                  class="mr-4"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Update Chat
                </v-btn>
                <v-btn
                  color="secondary"
                  @click="$router.back()"
                  :disabled="isSaving"
                  class="mr-4"
                >
                  <v-icon left>mdi-cancel</v-icon>
                  Cancel
                </v-btn>
                <v-btn
                  color="error"
                  @click="confirmDelete"
                  :disabled="isSaving"
                  outlined
                >
                  <v-icon left>mdi-delete</v-icon>
                  Delete Chat
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Chat updated successfully.</h3>
                      <br />
                      <v-btn color="success" @click="showSuccessDialog = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: red; font-size: 90px; width: 200px !important"
                >
                  mdi-alert-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: red">Confirm Delete</h1>
                      <br />
                      <h3>
                        Are you sure you want to delete this chat?
                      </h3>
                      <br />
                      <v-btn
                        color="error"
                        @click="deleteData"
                        :loading="isDeleting"
                        class="mr-4"
                      >
                        Yes, Delete
                      </v-btn>
                      <v-btn
                        color="secondary"
                        @click="showDeleteDialog = false"
                        :disabled="isDeleting"
                      >
                        Cancel
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ChatEditPage",
  data() {
    return {
      chat: null,
      form: {
        is_group: false,
        name: "",
        image_path: "",
        is_active: true,
      },
      errors: {},
      isLoading: true,
      isSaving: false,
      isDeleting: false,
      showSuccessDialog: false,
      showDeleteDialog: false,
    };
  },
  mounted() {
    this.fetchChat();
  },
  methods: {
    async fetchChat() {
      this.isLoading = true;
      try {
        const chatId = this.$route.params.id;
        const response = await axios.get(`/chats/${chatId}`);

        if (response.data.success) {
          this.chat = response.data.data;
          this.form = {
            is_group: this.chat.is_group,
            name: this.chat.name || "",
            image_path: this.chat.image_path || "",
            is_active: this.chat.is_active,
          };
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Chat not found",
          });
        }
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message: "Chat not found",
          });
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading chat data",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async updateData() {
      this.isSaving = true;
      this.errors = {};

      try {
        const chatData = {
          is_group: this.form.is_group,
          name: this.form.name || null,
          image_path: this.form.image_path || null,
          is_active: this.form.is_active,
        };

        const chatId = this.$route.params.id;
        const response = await axios.patch(
          `/chats/${chatId}`,
          chatData
        );

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.chat = response.data.data;
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while updating the chat.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while updating the chat.",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete() {
      this.showDeleteDialog = true;
    },

    async deleteData() {
      this.isDeleting = true;

      try {
        const chatId = this.$route.params.id;
        const response = await axios.post(
          `/chats/delete/${chatId}`
        );

        if (response.data.success) {
          this.showDeleteDialog = false;
          this.$router.push("/chats");
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while deleting the chat.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "An error occurred while deleting the chat.",
          });
        }
      } finally {
        this.isDeleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
  },
};
</script>

<style scoped>
.v-card-title {
  font-weight: bold;
  font-size: 1.5rem;
}

.v-text-field.outlined .v-input__control {
  min-height: 48px;
}
</style>